import { useContext } from "react";
import { LangContext } from "./LangContext";
import { relativeTime, hasKorean, pickKoreanTitle, cleanText } from "../lib/utils";

const CLS_META = {
  gun:       { label: "Smoking Gun",   color: "var(--pc-tier-gun)" },
  watershed: { label: "Watershed",     color: "var(--pc-tier-watershed)" },
  notable:   { label: "Notable",       color: "var(--pc-tier-notable)" },
  common:    { label: "Common Ground", color: "var(--pc-fg-4)" },
};
const DIR_COLORS = { up: "var(--pc-dir-up)", down: "var(--pc-dir-down)" };
const dirLabel = (d, lang) => {
  if (lang === "en") return d === "up" ? "Escalation" : d === "down" ? "De-escalation" : "Neutral";
  return d === "up" ? "확전 요인" : d === "down" ? "진정 요인" : "중립";
};

export default function PCNewsCard({ article, selected, onClick, onOverride, kindBadge }) {
  const lang = useContext(LangContext);
  const cm = CLS_META[article.cls] || CLS_META.common;
  const showHITL = ["gun", "watershed", "notable"].includes(article.cls);
  const time = article.publishedAt ? relativeTime(article.publishedAt) : article.time;
  // 언어 설정에 따라 헤드라인/서브라인 선택
  const headline = lang === "en"
    ? cleanText(article.hed)
    : cleanText(pickKoreanTitle(article.hed_ko, article.hed));
  const subline = lang === "en"
    ? null
    : (hasKorean(article.hed_ko) ? cleanText(article.hed) : "");

  return (
    <div
      onClick={() => onClick && onClick(article)}
      style={{
        padding: "20px 28px",
        cursor: "pointer",
        borderBottom: "1px solid var(--pc-line-3)",
        background: selected ? "rgba(255,255,255,0.018)" : "transparent",
        transition: "background 100ms",
        position: "relative",
      }}
    >
      {selected && (
        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: "var(--pc-fg-1)" }} />
      )}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DIR_COLORS[article.dir] || "var(--pc-fg-3)" }}>
              {dirLabel(article.dir, lang)}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: cm.color }}>
              {cm.label}
            </span>
            {kindBadge && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: ".08em",
                textTransform: "uppercase", color: kindBadge.color,
                padding: "1px 6px", border: `1px solid ${kindBadge.color}`, borderRadius: 2,
              }}>
                {kindBadge.label}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--pc-fg-2)" }}>{article.src}</span>
            {article.altSources?.map((s, i) => (
              <span key={i} style={{ fontSize: 11, color: "var(--pc-fg-3)", fontWeight: 500 }}>+{s.src || s}</span>
            ))}
            <span style={{ fontSize: 10, color: "var(--pc-fg-3)" }}>· {time}</span>
          </div>
        </div>
        {showHITL && onOverride && (
          <span style={{ display: "flex", flexShrink: 0 }}>
            {["up", "down"].map(d => (
              <button
                key={d}
                onClick={e => { e.stopPropagation(); onOverride(article, d); }}
                style={{
                  width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                  background: "transparent", border: "none", color: "var(--pc-fg-3)", fontSize: 12, opacity: 0.4, cursor: "pointer",
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = 0.9)}
                onMouseLeave={e => (e.currentTarget.style.opacity = 0.4)}
              >
                {d === "up" ? "↑" : "↓"}
              </button>
            ))}
          </span>
        )}
      </div>

      <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pc-fg-1)", lineHeight: 1.35, marginBottom: 4, letterSpacing: "-.005em" }}>{headline}</div>
      {subline && <div style={{ fontSize: 13, color: "rgba(122,119,115,.6)", lineHeight: 1.35 }}>{subline}</div>}

      {article.reasoning && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--pc-fg-2)", marginBottom: 6 }}>
            Why it matters
          </div>
          <div style={{ fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.55 }}>{article.reasoning}</div>
        </div>
      )}
    </div>
  );
}

export { CLS_META, DIR_COLORS, dirLabel };
